import React from 'react';
import { Helmet } from 'react-helmet';
import { MapPin, Clock, Users, Mountain } from 'lucide-react';
import Header from '../../components/ui/Header';

const TrailDetailsListeningToMedievalSerbia = () => {
  return (
    <>
      <Helmet>
        <title>Osluškujući damare srednjovekovne Srbije – Nemanjićevim nasleđem - Creative Twinning</title>
        <meta
          name="description"
          content="Otkrijte srednjovekovnu Srbiju kroz stazu koja vodi kroz UNESCO manastire, kraljevske zadužbine i prirodne atrakcije. Doživite duhovnost i istoriju Nemanjićeve dinastije." />
        <meta name="keywords" content="Nemanjići, srednjovekovna Srbija, Studenica, UNESCO, manastiri, Radočelo, kulturno nasleđe" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <div className="relative h-96 bg-cover bg-center" style={{ backgroundImage: `url('https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456459/2.6_2.JPG_mini_ooju6q.jpg')` }}>
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <h1 className="text-3xl md:text-5xl font-bold mb-4">
                  Osluškujući damare srednjovekovne Srbije
                </h1>
                <p className="text-xl md:text-2xl">
                  Nemanjićevim nasleđem kroz vekove
                </p>
              </div>
            </div>
          </div>

          {/* Trail Info */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Dužina</h3>
                <p className="text-gray-600">oko 7 km</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Clock className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Trajanje</h3>
                <p className="text-gray-600">3–4 sata hoda</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Mountain className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Težina</h3>
                <p className="text-gray-600">Srednje zahtevna</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                <h3 className="font-semibold text-gray-900">Za koga</h3>
                <p className="text-gray-600">Hodočasnike, planinare</p>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">O stazi</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Na obalama reke Studenice, između stena Radočela i šuma koje se spuštaju do vode, stoji tišina koja traje vekovima. 
                Ova staza povezuje vreme i prostor, prirodu i duhovnost. Hodajući njome, čuje se kako planina diše i kako se u tišini 
                čuju damari srednjovekovne Srbije — molitve, koraci monaha, šum reke koji nosi priču o Stefanu Nemanji i njegovim naslednicima.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Ovo nije samo put kroz planinu, već hodočašće kroz dušu jednog naroda.
              </p>
            </div>

            {/* Natural Attractions */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Prirodne atrakcije</h2>
              
              {/* Reka Studenica */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Reka Studenica – život koji teče kroz vekove</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Hladna i bistra, reka Studenica vijuga između šuma i stena, stvarajući male brzake i tihe virove. 
                      Njena voda odražava nebo, a na obalama se u proleće bele narcisi i cvetaju divlje trešnje.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      U letnjim mesecima ovo je mesto gde se čuje samo žubor i cvrkut ptica, dok rani jutarnji sati donose 
                      miris mahovine i vlažne zemlje. Ribolovci dolaze po pastrmku, a planinari po tišinu.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456496/reka_studenica_2.1-_wikipedia_ar81kp.jpg"
                      alt="Reka Studenica koja vijuga kroz planinski predeo sa čistom vodom reflektujući nebo"
                      className="w-full h-64 object-cover" />
                  </div>
                </div>
              </div>

              {/* Radočelo */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Radočelo – planina molitve i pogleda</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456493/Radocelo-Brvnara2.2-wikipedia_pkinim.jpg"
                      alt="Planina Radočelo sa padinama obraslim bukvom i jelom i tradicionalnom brvnarom"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Padine planine Radočelo čuvaju stazu i njene priče. Obrasle bukvom i jelom, u proleće mirišu na smolu, 
                      a u jesen liče na more boja. Najviši vrh, Krivača (1.643 m), pruža pogled na Goliju, Pešter i Ibar.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Na njenim padinama nekada su bile brojne bačije, a danas se mogu videti pastirske staze kojima su 
                      monasi i seljani prolazili do isposnica Svetog Save.
                    </p>
                  </div>
                </div>
              </div>

              {/* Košaninova jezera */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Košaninova jezera – planinski fenomen</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      U blizini staze nalaze se čuvena mala i velika Košaninova bara, prirodni fenomen koji menja svoj oblik 
                      s vremenom. Kada pada kiša, jezero se povlači, a kada sunce sija, voda ga ispunjava do ivica.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Ovaj ritam prirode meštani tumače kao „disanje planine". U sumrak, površina jezera pretvara se u 
                      ogledalo u kojem se spajaju planina i nebo.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456481/KOSANINOVA_JEZERA_2.3_izvor_studenica_info_ig87kh.jpg"
                      alt="Košaninova jezera okružena planinskim predjelom sa refleksijom neba u vodi"
                      className="w-full h-64 object-cover" />
                  </div>
                </div>
              </div>

              {/* Vodopadi Izubre */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Vodopadi Izubre – tajna u senci breza</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763227256/VODOPAD_IZUBRE_2.5-_ezyqju.jpg"
                      alt="Vodopadi Izubre sa pet slapova koji se obrušavaju kroz šumu breza i bukvi"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na dvadesetak kilometara od Studenice, u selu Bzovik, skrivaju se vodopadi Izubre — pet slapova koji se 
                      obrušavaju kroz šumu breza, bukvi i borova.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Do njih vodi uska staza, a miris vlažnog lišća i huk vode prate putnika do samog dna kanjona. 
                      Ovde je priroda najglasnija i najdivnija u isto vreme.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cultural Heritage */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Kulturno nasleđe</h2>
              
              {/* Manastir Studenica */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Manastir Studenica – majka srpskih manastira</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456459/2.6_2.JPG_mini_ooju6q.jpg"
                      alt="Manastir Studenica sa belim mermernim zidovima i plavim freskama u dolini reke"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      U dolini reke Studenice, među stenama koje liče na stubove vremena, nalazi se manastir koji vekovima sija 
                      belinom mermera i plavetnilom fresaka. Zadužbina Stefana Nemanje, osnivača srpske države, podignuta krajem XII veka.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Jedna je od najlepših građevina srpske srednjovekovne umetnosti i pod zaštitom je UNESCO-a. Mermerni zidovi 
                      „Bogorodičine crkve", plava freska Raspeća Hristovog i tišina dvorišta čine da se ovde oseća prisustvo večnosti.
                    </p>
                  </div>
                </div>
              </div>

              {/* Nemanjin vodovod */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Nemanjin vodovod – srednjovekovno čudo</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Iznad manastira, u šumi, nalaze se ostaci Nemanjinog vodovoda – mermernog kanala iz XIV veka koji je 
                      manastir snabdevao vodom. Kamenje je slagano bez maltera, precizno, s pažnjom kakvu danas retko viđamo.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Tokom vekova, voda je nastavila da teče, a priroda ga je polako prekrila mahovinom. 
                      Hodajući uz njega, kao da čuješ korake monaha i zveket kofa.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456486/Nemanjin_vodovod_2.7-dzenarika_net_izvor_zdzxsw.jpg"
                      alt="Ostaci Nemanjinog vodovoda od mermernog kamena prekrivenog mahovinom u šumi"
                      className="w-full h-64 object-cover" />
                  </div>
                </div>
              </div>

              {/* Crkva Svete Bogorodice u Dolcu */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Crkva Svete Bogorodice u Dolcu – molitva u dve etaže</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456464/2.8.JPG_mini_mimrnp.jpg"
                      alt="Crkva Svete Bogorodice u Dolcu jedinstvena po svojoj dvoetažnoj konstrukciji"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      U selu Dolac, nadomak Studenice, nalazi se crkva koja se razlikuje od svih drugih – izgrađena je na dve etaže, 
                      što je čini jedinstvenom u Srbiji. Podignuta od belog kamena, s malim prozorima i tisinim vratima.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Na zidovima su sačuvane senke fresaka, a s prozora se vidi reka. 
                      Ovo mesto nosi mir – i tihu lepotu prošlih vekova.
                    </p>
                  </div>
                </div>
              </div>

              {/* Isposnice Svetog Save */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Isposnice Svetog Save – tišina u steni</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Na padinama Radočela, duboko u šumi, nalaze se Donja i Gornja isposnica Svetog Save. 
                      Donja, skrivena među drvećem, okružena je tišinom koju samo reke mogu da prekinu.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Gornja, uklesana u stenu na mestu zvanom „Nemanjin toranj", čuva malu kapelu, pećinu i izvor poznat kao 
                      Savino bure – voda koja, veruje se, leči oči i vraća vid. Ovo je mesto gde se molitva ne izgovara, već udiše.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456466/2.10.JPG_mini_z4rs3z.jpg"
                      alt="Isposnica Svetog Save uklesana u stenu sa malom kapelom i izvorom Savino bure"
                      className="w-full h-64 object-cover" />
                  </div>
                </div>
              </div>

              {/* Petrova crkva */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Petrova crkva – svedok početaka Raške</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456491/PETROVA_CRKVA_2.10.1.jpg_mini_wueihv.jpg"
                      alt="Petrova crkva u Novom Pazaru, najstarija crkva u Srbiji iz IX veka"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <div className="flex items-center mb-2 text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>GPS: 43°08'40"N 20°30'24"E</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Najstarija crkva u Srbiji, podignuta u IX veku. Ona je tiha čuvarica vremena kada je Raška postajala 
                      duhovno i političko središte.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Odavde kreće priča o Nemanjićima i srpskoj državnosti.
                    </p>
                  </div>
                </div>
              </div>

              {/* Manastir Sopoćani */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Manastir Sopoćani – slika večnosti</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <div className="flex items-center mb-2 text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>GPS: 43°08'27"N 20°27'59"E</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Zadužbina kralja Uroša I, izgrađena u XIII veku, pod zaštitom UNESCO-a. 
                      Poznat po freskama koje prikazuju najlepše prikaze iz vizantijske umetnosti.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      U dolini Raške, među brežuljcima, Sopoćani odišu spokojem i božanskom lepotom.
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456501/SOPOCANI_2.10.2.jpg_mini_vhyexf.jpg"
                      alt="Manastir Sopoćani u dolini Raške sa čuvenim vizantijskim freskama"
                      className="w-full h-64 object-cover" />
                  </div>
                </div>
              </div>

              {/* Manastir Đurđevi Stupovi */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Manastir Đurđevi Stupovi – stubovi neba</h3>
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456471/DJURDJEVI_STUPOVI_2.10.3.jpg_mini_v9afr8.jpg"
                      alt="Manastir Đurđevi Stupovi sa dva vitka stuba na brdu iznad Novog Pazara"
                      className="w-full h-64 object-cover" />
                  </div>
                  <div>
                    <div className="flex items-center mb-2 text-gray-600">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>GPS: 43°08'56"N 20°29'47"E</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Zadužbina Stefana Nemanje, smeštena na brdu iznad Novog Pazara. 
                      S dva vitka stuba koja se uzdižu ka nebu, simbolizuje trajnost i veru jednog naroda.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      S ovog mesta pogled puca na čitavu dolinu Raške.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Itinerary */}
            <div className="bg-white rounded-lg shadow-md p-8 mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Predlog programa posete</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Osnovne informacije</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Dužina staze:</strong> oko 7 km</li>
                    <li><strong>Trajanje:</strong> 3–4 sata hoda</li>
                    <li><strong>Težina:</strong> srednje zahtevna</li>
                    <li><strong>Preporučeno za:</strong> planinare, hodočasnike, ljubitelje istorije i prirode</li>
                    <li><strong>Najbolje vreme:</strong> proleće i rana jesen</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Predlog obilaska</h3>
                  <ol className="space-y-2 text-gray-700 list-decimal list-inside">
                    <li>Početak kod Manastira Studenica</li>
                    <li>Uspon kroz šumu do Nemanjinog vodovoda</li>
                    <li>Poseta crkvi Svete Bogorodice u Dolcu</li>
                    <li>Odmor kod izvora Svetog Save</li>
                    <li>Povratak u Studenicu i poseta manastirskoj galeriji</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Accommodation and Dining */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Gde jesti i prespavati</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Restaurants */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Restorani</h3>
                  
                  <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4 items-center">
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763227256/restoran_studenica_2.11-_google_search_pypk0j.webp"
                          alt="Restoran Studenica sa tradicionalnim ambijentom i pogledom na prirodu"
                          className="w-full h-32 object-cover" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Restoran Studenica</h4>
                        <p className="text-sm text-gray-600 mb-2">📍 Brezova 7, 36222 Kraljevo</p>
                        <p className="text-sm text-gray-600 mb-2">📞 +381 65 6880001</p>
                        <p className="text-gray-700 text-sm">
                          Na obali reke, pod krošnjama oraha. Hrana se priprema po starim receptima – jednostavno, iskreno i s merom.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Accommodation */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Smeštaj</h3>
                  
                  <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4 items-center">
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456479/hotel-radocelo_2.12._h95441.jpg"
                          alt="Hotel Radočelo planinski hotel sa ribnjakom i terasom iznad vode"
                          className="w-full h-32 object-cover" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Hotel Radočelo</h4>
                        <p className="text-sm text-gray-600 mb-2">📍 Miliće bb, 36222 Kraljevo</p>
                        <p className="text-sm text-gray-600 mb-2">📞 +381 36 438115</p>
                        <p className="text-gray-700 text-sm">
                          Planinski hotel s ribnjakom i terasom iznad vode. Zimi puca vatra u kaminu, leti se večera pod zvezdama.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4 items-center">
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456469/2.14.JPG_mini_txkkqf.jpg"
                          alt="Konak Manastira Studenica sa tradicionalnom arhitekturom u prirodnom okruženju"
                          className="w-full h-32 object-cover" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Konak Manastira Studenica</h4>
                        <p className="text-sm text-gray-600 mb-2">📍 Studenica, Brezova, 36222 Kraljevo</p>
                        <p className="text-sm text-gray-600 mb-2">📞 +381 64 6467492</p>
                        <p className="text-gray-700 text-sm">
                          Za one koji traže mir i duhovno utočište. Ujutru se čuje pojanje monaha i zvono koje poziva na molitvu.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4 items-center">
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456474/HOTEL_GOLUB.jpg_mini_ahn6hl.jpg"
                          alt="Hotel Golub"
                          className="w-full h-32 object-cover" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Hotel Golub</h4>
                        <p className="text-sm text-gray-600 mb-2">📍 Odvraćenica bb, 32260 Ivanjica</p>
                        <p className="text-sm text-gray-600 mb-2">📞 +381 66 6776 311</p>
                        <p className="text-gray-700 text-sm">
                          Na visoravni Odvraćenica, okružen borovima i tišinom. Savršen za planinare i one koji traže spokoj.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4 items-center">
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763227254/hotel_sedlo_ky6okj.jpg"
                          alt="Hotel Sedlo u srcu Parka prirode Golija sa spa zonom i pogledom na šumske padine"
                          className="w-full h-32 object-cover" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Hotel Sedlo</h4>
                        <p className="text-sm text-gray-600 mb-2">📍 Plešin 43, 36350 Raška</p>
                        <p className="text-sm text-gray-600 mb-2">📞 +381 36 515 0111</p>
                        <p className="text-gray-700 text-sm">
                          U srcu Parka prirode Golija. Gosti mogu da uživaju u toplom enterijeru, spa zoni i pogledu na šumske padine.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-4 items-center">
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763456484/MOTEL_KU%C4%8C.jpg_mini_zbhh2l.jpg"
                          alt="Motel Kuč staro planinsko odmorište koje je zadržalo autentičnost Golije"
                          className="w-full h-32 object-cover" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Motel Kuč</h4>
                        <p className="text-sm text-gray-600 mb-2">📍 Odvraćenica bb, 32260 Ivanjica</p>
                        <p className="text-sm text-gray-600 mb-2">📞 +381 64 554 7707</p>
                        <p className="text-sm text-gray-600 mb-2">✉️ motel.kuc@gmail.com</p>
                        <p className="text-sm text-gray-600 mb-2">📍 GPS: 43°28'12"N 20°18'22"E</p>
                        <p className="text-gray-700 text-sm mb-2">
                          Na putu između Odvraćenice i Studenice nalazi se „Motel Kuč", staro planinsko odmorište koje je zadržalo autentičnost Golije.
                        </p>
                        <p className="text-gray-700 text-sm">
                          U prizemlju miris domaće kuhinje, na spratu sobe sa pogledom na šumu. Topla atmosfera, tradicionalna hrana i blizina svih glavnih staza čine ga savršenim mestom za predah.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Must See */}
              <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Ne propustite</h3>
                <ul className="grid md:grid-cols-2 gap-2 text-gray-700">
                  <li>• Manastir Studenicu (UNESCO)</li>
                  <li>• Isposnice Svetog Save</li>
                  <li>• Crkvu Svete Bogorodice u Dolcu</li>
                  <li>• Nemanjin vodovod</li>
                  <li>• Vodopade Izubre i Košaninova jezera</li>
                  <li>• Radočelski sabor i umetničku koloniju Studenica</li>
                </ul>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default TrailDetailsListeningToMedievalSerbia;